import { PureComponent } from 'react';
import isObject from 'lodash/isObject';
import debounce from 'lodash/debounce';
import { isArrayLike } from 'mobx';
import { matchMediaPolifill } from '../_util/mediaQueryListPolyfill';
import { Breakpoint } from './enum';
let enquire;
if (typeof window !== 'undefined') {
    window.matchMedia = window.matchMedia || matchMediaPolifill;
    // eslint-disable-next-line global-require
    enquire = require('enquire.js');
}
const responsiveMap = {
    [Breakpoint.xxl]: '(min-width: 1600px)',
    [Breakpoint.xl]: '(min-width: 1200px)',
    [Breakpoint.lg]: '(min-width: 992px)',
    [Breakpoint.md]: '(min-width: 768px)',
    [Breakpoint.sm]: '(min-width: 576px)',
    [Breakpoint.xs]: '(max-width: 575px)',
};
const responsiveArray = Object.keys(responsiveMap);
function isBreakPointMap(item) {
    if (isObject(item)) {
        const keys = Object.keys(item);
        if (keys.length) {
            return keys.every(key => key in responsiveMap);
        }
    }
    return false;
}
export function hasBreakPointMap(items) {
    // eslint-disable-next-line no-use-before-define
    return items.some(isOrHasBreakPointMap);
}
function isOrHasBreakPointMap(item) {
    return isArrayLike(item) ? hasBreakPointMap(item) : isBreakPointMap(item);
}
export default class Responsive extends PureComponent {
    constructor() {
        super(...arguments);
        this.state = { breakpoints: {} };
        this.handlers = new Map();
        this.fireUpdate = debounce(() => {
            const { onChange } = this.props;
            if (onChange) {
                onChange(this.getValues());
            }
        }, 10);
    }
    isDisabled(props) {
        const { disabled, items } = props;
        if (!disabled && items) {
            return !hasBreakPointMap(items);
        }
        return true;
    }
    componentDidMount() {
        if (!this.isDisabled(this.props)) {
            this.register();
        }
    }
    componentDidUpdate(prevProps) {
        const { props } = this;
        if (!this.isDisabled(prevProps) && this.isDisabled(props)) {
            this.unregister();
        }
        if (this.isDisabled(prevProps) && !this.isDisabled(props)) {
            this.register();
        }
    }
    componentWillUnmount() {
        if (!this.isDisabled(this.props)) {
            this.unregister();
        }
    }
    register() {
        if (enquire) {
            const { handlers } = this;
            responsiveArray.forEach((breakpoint) => {
                const query = responsiveMap[breakpoint];
                const handler = {
                    match: () => {
                        this.setState(prevState => ({
                            breakpoints: {
                                ...prevState.breakpoints,
                                [breakpoint]: true,
                            },
                        }), this.fireUpdate);
                    },
                    unmatch: () => {
                        this.setState(prevState => ({
                            breakpoints: {
                                ...prevState.breakpoints,
                                [breakpoint]: false,
                            },
                        }), this.fireUpdate);
                    },
                    // Keep a empty destory to avoid triggering unmatch when unregister
                    destroy() {
                    },
                };
                enquire.register(query, handler);
                handlers.set(query, handler);
            });
        }
    }
    unregister() {
        const { handlers } = this;
        Object.keys(responsiveMap).forEach((breakpoint) => {
            const query = responsiveMap[breakpoint];
            const handler = handlers.get(query);
            if (handler) {
                enquire.unregister(query, handler);
            }
        });
    }
    processValue(value) {
        const { breakpoints } = this.state;
        if (isArrayLike(value) && hasBreakPointMap(value)) {
            return value.map(this.processValue, this);
        }
        if (isBreakPointMap(value)) {
            for (let i = 0; i < responsiveArray.length; i++) {
                const breakpoint = responsiveArray[i];
                if (breakpoints[breakpoint] && value[breakpoint] !== undefined) {
                    return value[breakpoint];
                }
            }
            return undefined;
        }
        return value;
    }
    getValues() {
        const { items } = this.props;
        if (items) {
            return items.map(this.processValue, this);
        }
        return [];
    }
    render() {
        const { props } = this;
        const { children } = props;
        if (typeof children === 'function') {
            return children(this.getValues(), this.isDisabled(props));
        }
        return children;
    }
}
Responsive.displayName = 'Responsive';
